import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useOpenSession } from "@src/api/useOpenSession";
import idCard from "@src/assets/icons/IdCard.svg";
import clock from "@src/assets/icons/clock.svg";
import file from "@src/assets/icons/file.svg";
import passport from "@src/assets/icons/passport.svg";
import Container from "@src/components/shared/Container";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import useZustandStore from "@src/helpers/zustand";
import { WITH_PAYUNICARD } from "@src/utils/consts";
import { axiosInstance } from "@src/utils/axios/AxiosProvider";

interface TListItem {
  src: string;
  children: JSX.Element;
}

function ListItem({ src, children }: TListItem) {
  return (
    <div className="flex items-center gap-4">
      <div className="bg-alpineGoat flex h-9 w-9 items-center justify-center rounded-lg md:h-10 md:w-10">
        <img alt="clock" className="h-[26px] w-[26px]" src={src} />
      </div>
      {children}
    </div>
  );
}

function First() {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { data, setData, clearData } = useZustandStore();
  const openSessionMutation = useOpenSession();

  const urlToken = hash.replace("#", "");

  useEffect(() => {
    if (urlToken) {
      axiosInstance
        .post(
          "/api/ManualKyc/IsManual",
          {},
          {
            headers: {
              "x-api-key": urlToken,
            },
          }
        )
        .then((res) => {
          setData({
            ...data,
            isManual: res?.data?.isManual,
          });
        })
        .catch((err) => {
          navigate(`/${i18n.language}/error`, {
            state: {
              title: t("error.title"),
              description: err?.response?.data?.DisplayText,
              errorBtn: "show",
            },
          });
        });
    }
  }, [urlToken]);

  function onSubmit() {
    const apiKey = data?.isManual ? urlToken : hash.substring(1);
    const uuid = crypto.randomUUID();
    if (apiKey) {
      if (WITH_PAYUNICARD) {
        setData({
          ...data,
          apiKey,
          contextId: uuid,
        });
        // navigate(`/${i18n.language}/identify-with-payunicard`);
        navigate(`/${i18n.language}/terms`);
      } else {
        openSessionMutation.mutate(apiKey);
      }
    } else {
      navigate(`/${i18n.language}/404`);
    }
  }

  useEffect(() => {
    clearData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { t, i18n } = useTranslation();
  return (
    <Container className="max-md:min-h-[600px] max-md:pb-[200px] relative">
      <div className="py-4 max-sm:mt-14 ">
        <TitleAndDesc desc={t("first.desc")} title={t("first.title")} />
      </div>
      <div className="rounded-[6px] bg-white p-4 md:px-0">
        <ListItem src={clock}>
          <p className="text-textBlack text-medium text-sm">
            {t("first.verification")}
          </p>
        </ListItem>
      </div>

      <div className="rounded-[6px] bg-white p-4 md:px-0">
        <p className="mb-2 font-medium">{t("first.info.title")}</p>
        <div className="flex flex-col gap-3">
          <ListItem src={idCard}>
            <div className="flex items-center gap-1">
              <p className="text-textBlack text-medium text-sm">
                {t("first.info.idCard")}
              </p>
              <p className="text-textGray text-xs">({t("first.info.geo")})</p>
            </div>
          </ListItem>
          <ListItem src={file}>
            <div className="flex items-center gap-1">
              <p className="text-textBlack text-medium text-sm">
                {t("first.info.residence")}
              </p>
              <p className="text-textGray text-xs">({t("first.info.geo")})</p>
            </div>
          </ListItem>
          <ListItem src={passport}>
            <p className="text-textBlack text-medium text-sm">
              {t("first.info.passport")}
            </p>
          </ListItem>
        </div>
      </div>
      <div
        className="w-full self-end rounded-[6px] bg-white py-8
       max-md:absolute max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:px-4"
      >
        <Button
          className="w-full"
          disabled={openSessionMutation.isLoading}
          onClick={onSubmit}
        >
          {t("startIdentification.next")}
        </Button>
      </div>
    </Container>
  );
}

export default First;
